import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["leftVideo", "rightVideo", "playButton", "seek", "timeLabel"]

  connect() {
    if (!this.hasLeftVideoTarget || !this.hasRightVideoTarget) return

    this.leftVideoTarget.muted = true
    this.rightVideoTarget.muted = false
    this.handleTimeUpdate = this.updateProgress.bind(this)
    this.handleEnded = this.pauseBoth.bind(this)
    this.handleMetadata = this.updateProgress.bind(this)

    this.rightVideoTarget.addEventListener("timeupdate", this.handleTimeUpdate)
    this.rightVideoTarget.addEventListener("ended", this.handleEnded)
    this.leftVideoTarget.addEventListener("ended", this.handleEnded)
    this.videos.forEach((video) => video.addEventListener("loadedmetadata", this.handleMetadata))
    this.updateProgress()
  }

  disconnect() {
    if (!this.handleTimeUpdate) return

    this.rightVideoTarget.removeEventListener("timeupdate", this.handleTimeUpdate)
    this.rightVideoTarget.removeEventListener("ended", this.handleEnded)
    this.leftVideoTarget.removeEventListener("ended", this.handleEnded)
    this.videos.forEach((video) => video.removeEventListener("loadedmetadata", this.handleMetadata))
  }

  togglePlayback() {
    if (this.videos.some((video) => !video.paused)) {
      this.pauseBoth()
    } else {
      this.playBoth()
    }
  }

  restart() {
    this.pauseBoth()
    this.videos.forEach((video) => { video.currentTime = 0 })
    this.updateProgress()
    this.playBoth()
  }

  seek(event) {
    const duration = this.comparisonDuration
    if (!duration) return

    const position = (Number(event.currentTarget.value) / 1000) * duration
    this.videos.forEach((video) => { video.currentTime = Math.min(position, video.duration || position) })
    this.updateProgress()
  }

  async playBoth() {
    if (!this.comparisonDuration) return

    const masterTime = this.rightVideoTarget.currentTime
    if (masterTime >= this.comparisonDuration - 0.05) {
      this.videos.forEach((video) => { video.currentTime = 0 })
    } else {
      this.leftVideoTarget.currentTime = masterTime
    }

    await Promise.allSettled(this.videos.map((video) => video.play()))
    this.playButtonTarget.textContent = "Pause"
  }

  pauseBoth() {
    this.videos.forEach((video) => video.pause())
    if (this.hasPlayButtonTarget) this.playButtonTarget.textContent = "Play"
    this.updateProgress()
  }

  updateProgress() {
    const duration = this.comparisonDuration
    if (!duration) return

    const masterTime = Math.min(this.rightVideoTarget.currentTime, duration)
    const drift = Math.abs(this.leftVideoTarget.currentTime - masterTime)
    if (!this.leftVideoTarget.paused && drift > 0.12) this.leftVideoTarget.currentTime = masterTime

    this.seekTarget.value = Math.round((masterTime / duration) * 1000)
    this.timeLabelTarget.textContent = `${this.formatTime(masterTime)} / ${this.formatTime(duration)}`
    if (masterTime >= duration - 0.03) {
      this.videos.forEach((video) => video.pause())
      if (this.hasPlayButtonTarget) this.playButtonTarget.textContent = "Play"
    }
  }

  get videos() {
    return [this.leftVideoTarget, this.rightVideoTarget]
  }

  get comparisonDuration() {
    const durations = this.videos.map((video) => video.duration).filter(Number.isFinite)
    return durations.length === 2 ? Math.min(...durations) : 0
  }

  formatTime(seconds) {
    const safeSeconds = Number.isFinite(seconds) ? Math.max(0, seconds) : 0
    const minutes = Math.floor(safeSeconds / 60)
    const remainder = Math.floor(safeSeconds % 60).toString().padStart(2, "0")
    return `${minutes}:${remainder}`
  }
}
